import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Grid,
    Paper,
    IconButton,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { RestaurantService } from '../../../api/services/restaurantService';
import { RoomService } from '../../../api/services/roomService';
import { HotelService } from '../../../api/services/hotelService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { formatCurrency, useCurrency, getActiveCurrency } from '../../../utils/currency';
import { isUaeProperty, extractUaeCustomRates, calculateDubaiItemizedChargeTax, DUBAI_DEFAULT_RATES } from '../../../utils/dubaiHospitalityTax';

export default function CreateOrderView({ onBack, onSuccess }) {
    const authData = (() => {
        try {
            return JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {
            return {};
        }
    })();
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));

    const { currency } = useCurrency();
    const activeCurrency = getActiveCurrency();
    const isUae = currency === 'AED' || 
                  activeCurrency === 'AED' || 
                  tenant?.currency === 'AED' || 
                  authData?.currency === 'AED' || 
                  authData?.hotel?.currency === 'AED' || 
                  authData?.tenant?.currency === 'AED' || 
                  isUaeProperty(tenant) || 
                  isUaeProperty(authData) || 
                  isUaeProperty(authData?.hotel) || 
                  isUaeProperty(authData?.tenant);

    const hotelStatus = tenant?.status || 'Active';
    const isRoomChargeLocked = ['Inactive', 'Suspended', 'Archived'].includes(hotelStatus);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [menuItems, setMenuItems] = useState([]);
    const [activeRooms, setActiveRooms] = useState([]);

    const [form, setForm] = useState({
        roomId: '',
        items: [
            { menuItemId: '', quantity: 1, unitPrice: '' }
        ]
    });

    const [isSpecifiedPremises, setIsSpecifiedPremises] = useState(false);
    const [fbTaxRate, setFbTaxRate] = useState(isUae ? 5 : 0);
    const [fbSpecifiedTaxRate, setFbSpecifiedTaxRate] = useState(18);
    const [liquorVatRate, setLiquorVatRate] = useState(22);
    const [rawTaxes, setRawTaxes] = useState([]);
    const [uaeRates, setUaeRates] = useState(DUBAI_DEFAULT_RATES);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                // Fetch items
                const response = await RestaurantService.getMenuItems({ noPagination: true });
                const dataArray = response?.data?.data || response?.data || response?.items || [];
                setMenuItems(Array.isArray(dataArray) ? dataArray : []);
            } catch (error) {
                console.error('Failed to load menu items:', error);
            }
        };

        const fetchRooms = async () => {
            try {
                // Fetch rooms from PMS
                const response = await RoomService.getAll({ noPagination: true });
                const roomsArray = response?.data?.data || response?.data || response?.rooms || [];
                setActiveRooms(Array.isArray(roomsArray) ? roomsArray : []);
            } catch (error) {
                console.error('Failed to fetch rooms:', error);
            }
        };

        const fetchHotelAndTaxes = async () => {
            try {
                const hotelId = tenant?.id || tenant?._id || authData?.hotelId || authData?.hotel?.id || authData?.hotel?._id;
                if (hotelId) {
                    try {
                        const hotelResponse = await HotelService.getById(hotelId, tenantId);
                        const hotelData = hotelResponse?.data || hotelResponse;
                        // Specified premises applies ONLY to Indian GST properties
                        if (!isUae) {
                            setIsSpecifiedPremises(Boolean(hotelData?.isSpecifiedPremises));
                        } else {
                            setIsSpecifiedPremises(false);
                        }
                    } catch (hErr) {
                        console.warn("Failed to fetch hotel details:", hErr);
                    }
                } else {
                    if (isUae) setIsSpecifiedPremises(false);
                }

                // Fetch tax slabs from API (https://api-pms.hotelss.in/api/v1/settings/tax-slabs?noPagination=true)
                const taxResponse = await SettingsService.getTaxes(tenantId, { noPagination: true });
                const taxes = taxResponse?.data?.data || taxResponse?.data || taxResponse || [];
                
                if (Array.isArray(taxes)) {
                    setRawTaxes(taxes);
                    if (isUae) {
                        // Extract UAE statutory rates from API tax slabs
                        const customRates = extractUaeCustomRates(taxes);
                        setUaeRates({
                            serviceChargePct: customRates.serviceChargePct !== undefined ? Number(customRates.serviceChargePct) : DUBAI_DEFAULT_RATES.serviceChargePct,
                            municipalityFeePct: customRates.municipalityFeePct !== undefined ? Number(customRates.municipalityFeePct) : DUBAI_DEFAULT_RATES.municipalityFeePct,
                            vatPct: customRates.vatPct !== undefined ? Number(customRates.vatPct) : DUBAI_DEFAULT_RATES.vatPct
                        });
                        if (customRates.vatPct !== undefined && !isNaN(customRates.vatPct)) {
                            setFbTaxRate(Number(customRates.vatPct));
                        } else {
                            const activeVat = taxes.find(t => (t.status === 'Active' || t.isActive) && 
                                (t.serviceType === 'vat' || String(t.name || '').toLowerCase().includes('vat') || t.serviceType === 'f_and_b'));
                            if (activeVat) {
                                const r = activeVat.rate !== undefined && activeVat.rate !== null && activeVat.rate !== '' ? activeVat.rate : activeVat.gstPct;
                                if (r !== undefined && !isNaN(Number(r))) setFbTaxRate(Number(r));
                            } else {
                                setFbTaxRate(5);
                            }
                        }
                    } else {
                        // Preserved for Indian tenant:
                        const activeFb = taxes.find(t => (t.status === 'Active' || t.isActive) && t.serviceType === 'f_and_b');
                        if (activeFb) {
                            const r = activeFb.rate !== undefined && activeFb.rate !== null && activeFb.rate !== '' ? activeFb.rate : activeFb.gstPct;
                            if (r !== undefined) setFbTaxRate(Number(r));
                        }
                        
                        const activeFbSpec = taxes.find(t => (t.status === 'Active' || t.isActive) && t.serviceType === 'f_and_b_specified');
                        if (activeFbSpec) {
                            const r = activeFbSpec.rate !== undefined && activeFbSpec.rate !== null && activeFbSpec.rate !== '' ? activeFbSpec.rate : activeFbSpec.gstPct;
                            if (r !== undefined) setFbSpecifiedTaxRate(Number(r));
                        }
                        
                        const activeLiquor = taxes.find(t => (t.status === 'Active' || t.isActive) && t.serviceType === 'liquor_vat');
                        if (activeLiquor) {
                            const r = activeLiquor.rate !== undefined && activeLiquor.rate !== null && activeLiquor.rate !== '' ? activeLiquor.rate : activeLiquor.gstPct;
                            if (r !== undefined) setLiquorVatRate(Number(r));
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch hotel tax config:", err);
                if (isUae) setFbTaxRate(5);
            }
        };

        fetchItems();
        fetchRooms();
        fetchHotelAndTaxes();
    }, [tenantId, isUae]);

    const handleRoomChange = (e) => {
        setForm({ ...form, roomId: e.target.value });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...form.items];
        newItems[index][field] = value;

        // Auto-populate price when item is selected
        if (field === 'menuItemId') {
            const selectedItem = menuItems.find(i => i.id === value);
            if (selectedItem) {
                newItems[index].unitPrice = selectedItem.price || selectedItem.basePrice || 0;
            }
        }
        
        setForm({ ...form, items: newItems });
    };

    const addLineItem = () => {
        setForm({
            ...form,
            items: [...form.items, { menuItemId: '', quantity: 1, unitPrice: '' }]
        });
    };

    const removeLineItem = (index) => {
        const newItems = form.items.filter((_, i) => i !== index);
        setForm({ ...form, items: newItems });
    };

    const getTaxInfo = (menuItemId) => {
        if (!menuItemId) return null;
        const menuItem = menuItems.find(mi => mi.id === menuItemId);
        const isAlcohol = menuItem ? menuItem.isAlcohol : false;
        
        if (isUae) {
            const vat = (fbTaxRate !== undefined && fbTaxRate !== null && fbTaxRate > 0) ? fbTaxRate : 5;
            return { rate: vat, label: `UAE VAT (${vat}%)` };
        }

        // Indian tenant GST logic (strictly preserved)
        if (isAlcohol) return { rate: liquorVatRate, label: 'Alcohol Tax' };
        if (isSpecifiedPremises) return { rate: fbSpecifiedTaxRate, label: 'Luxury F&B Tax' };
        
        return { rate: fbTaxRate, label: 'F&B Tax' };
    };

    const calculateTotals = () => {
        let subtotal = 0;
        let serviceChargeTotal = 0;
        let municipalityFeeTotal = 0;
        let vatTotal = 0;
        let taxTotal = 0;
        const taxBreakdown = {};

        form.items.forEach(item => {
            const q = parseInt(item.quantity || 0, 10);
            const p = parseFloat(item.unitPrice || 0);
            const lineTotal = q * p;
            subtotal += lineTotal;

            if (item.menuItemId) {
                if (isUae) {
                    const uaeCalc = calculateDubaiItemizedChargeTax({
                        baseAmount: lineTotal,
                        category: 'Food & Beverage (F&B)',
                        taxes: rawTaxes
                    });
                    serviceChargeTotal += uaeCalc.serviceChargeAmount;
                    municipalityFeeTotal += uaeCalc.municipalityFeeAmount;
                    vatTotal += uaeCalc.vatAmount;
                    taxTotal += uaeCalc.totalTaxAmount;
                } else {
                    const taxInfo = getTaxInfo(item.menuItemId);
                    const applicableTaxPct = taxInfo ? taxInfo.rate : 0;

                    const taxAmount = (lineTotal * applicableTaxPct) / 100;
                    taxTotal += taxAmount;

                    if (applicableTaxPct > 0) {
                        const halfRate = applicableTaxPct / 2;
                        if (!taxBreakdown[halfRate]) {
                            taxBreakdown[halfRate] = 0;
                        }
                        taxBreakdown[halfRate] += (taxAmount / 2);
                    }
                }
            }
        });

        const subtotalBeforeVat = subtotal + serviceChargeTotal + municipalityFeeTotal;
        const grandTotal = Number((subtotal + taxTotal).toFixed(2));

        return {
            subtotal,
            serviceChargeTotal: Number(serviceChargeTotal.toFixed(2)),
            municipalityFeeTotal: Number(municipalityFeeTotal.toFixed(2)),
            subtotalBeforeVat: Number(subtotalBeforeVat.toFixed(2)),
            vatTotal: Number(vatTotal.toFixed(2)),
            taxTotal: Number(taxTotal.toFixed(2)),
            taxBreakdown,
            grandTotal
        };
    };

    const totals = calculateTotals();

    const handleSubmit = async () => {
        if (!form.roomId) {
            toast.error('Please select a target room');
            return;
        }

        // Clean payload strictly
        const payloadItems = form.items
            .filter(item => item.menuItemId && item.quantity > 0 && item.unitPrice !== '')
            .map(item => ({
                menuItemId: item.menuItemId,
                quantity: parseInt(item.quantity, 10),
                unitPrice: parseFloat(item.unitPrice)
            }));

        if (payloadItems.length === 0) {
            toast.error('Please add at least one valid menu item to the order');
            return;
        }

        setIsSubmitting(true);
        try {
            await RestaurantService.createRestaurantOrder({
                roomId: form.roomId,
                items: payloadItems
            });
            toast.success('Restaurant order successfully created and posted to room folio');
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Failed to create order:', error);
            if (error.response?.status === 403 || error.response?.status === 405) {
                toast.error('Action not allowed while the hotel is suspended.');
            } else {
                toast.error(error.message || 'Failed to create order.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }} disabled={isSubmitting}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight="bold">Create New Order</Typography>
            </Box>

            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid #eee', elevation: 0 }}>
                {/* Master Order Assignment Block */}
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>Master Order Assignment</Typography>
                
                {isRoomChargeLocked && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <b>Room Charges Disabled:</b> The property status is currently {hotelStatus}. You cannot post charges to guest folios. Walk-in cash/card payments must be processed externally.
                    </Alert>
                )}

                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required disabled={isSubmitting || isRoomChargeLocked}>
                            <InputLabel>Charge To Room (Guest Folio)</InputLabel>
                            <Select
                                value={form.roomId}
                                label="Charge To Room (Guest Folio)"
                                onChange={handleRoomChange}
                            >
                                {activeRooms.map(room => (
                                    <MenuItem key={room.id} value={room.id}>
                                        Room {room.roomNumber} - {room.type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Line-Items Array Repeater Block */}
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>Order Line Items</Typography>
                
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Sl</TableCell>
                                <TableCell>Dish</TableCell>
                                <TableCell align="center">Qty</TableCell>
                                <TableCell align="right">Rate</TableCell>
                                {isUae ? (
                                    <>
                                        <TableCell align="right">SC ({uaeRates.serviceChargePct}%)</TableCell>
                                        <TableCell align="right">Mun ({uaeRates.municipalityFeePct}%)</TableCell>
                                        <TableCell align="right">VAT ({uaeRates.vatPct}%)</TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell align="center">CGST</TableCell>
                                        <TableCell align="center">SGST</TableCell>
                                        <TableCell align="right">Tax Amt</TableCell>
                                    </>
                                )}
                                <TableCell align="right">Total Amount</TableCell>
                                <TableCell align="center"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.items.map((item, index) => {
                                const q = parseInt(item.quantity || 0, 10);
                                const p = parseFloat(item.unitPrice || 0);
                                const lineTotal = q * p;

                                let uaeCalc = null;
                                let taxAmount = 0;
                                let finalAmount = lineTotal;
                                let halfRate = 0;

                                if (item.menuItemId) {
                                    if (isUae) {
                                        uaeCalc = calculateDubaiItemizedChargeTax({
                                            baseAmount: lineTotal,
                                            category: 'Food & Beverage (F&B)',
                                            taxes: rawTaxes
                                        });
                                        finalAmount = uaeCalc.totalPostAmount;
                                    } else {
                                        const taxInfo = getTaxInfo(item.menuItemId);
                                        const applicableTaxPct = taxInfo ? taxInfo.rate : 0;
                                        halfRate = applicableTaxPct / 2;
                                        taxAmount = (lineTotal * applicableTaxPct) / 100;
                                        finalAmount = lineTotal + taxAmount;
                                    }
                                }
                                
                                return (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <FormControl fullWidth disabled={isSubmitting} size="small" variant="standard">
                                                <Select
                                                    value={item.menuItemId}
                                                    onChange={(e) => handleItemChange(index, 'menuItemId', e.target.value)}
                                                    disableUnderline
                                                    displayEmpty
                                                >
                                                    <MenuItem value="" disabled>Select Menu Item</MenuItem>
                                                    {menuItems.map(mi => (
                                                        <MenuItem key={mi.id} value={mi.id}>
                                                            {mi.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 70 }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                variant="standard"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', Math.max(1, parseInt(e.target.value || 1, 10)))}
                                                InputProps={{ inputProps: { min: 1, step: 1, style: { textAlign: 'center' } }, disableUnderline: true }}
                                                disabled={isSubmitting}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 90 }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                variant="standard"
                                                value={item.unitPrice}
                                                onChange={(e) => handleItemChange(index, 'unitPrice', Math.max(0, parseFloat(e.target.value || 0)))}
                                                InputProps={{ inputProps: { min: 0, step: "0.01", style: { textAlign: 'right' } }, disableUnderline: true }}
                                                disabled={isSubmitting}
                                            />
                                        </TableCell>
                                        {isUae ? (
                                            <>
                                                <TableCell align="right" sx={{ width: 85 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatCurrency(uaeCalc?.serviceChargeAmount || 0, currency)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: 85 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatCurrency(uaeCalc?.municipalityFeeAmount || 0, currency)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: 85 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatCurrency(uaeCalc?.vatAmount || 0, currency)}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell align="center" sx={{ width: 60 }}>
                                                    <Typography variant="body2" color="text.secondary">{halfRate}%</Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={{ width: 60 }}>
                                                    <Typography variant="body2" color="text.secondary">{halfRate}%</Typography>
                                                </TableCell>
                                                <TableCell align="right" sx={{ width: 90 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatCurrency(taxAmount)}
                                                    </Typography>
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell align="right" sx={{ width: 110 }}>
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(finalAmount, currency)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 50 }}>
                                            <IconButton 
                                                color="error" 
                                                size="small"
                                                onClick={() => removeLineItem(index)}
                                                disabled={isSubmitting || form.items.length === 1}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Button 
                    startIcon={<AddIcon />} 
                    variant="outlined" 
                    onClick={addLineItem}
                    disabled={isSubmitting}
                    sx={{ mt: 1 }}
                >
                    Add Item to Order
                </Button>

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                    <Box sx={{ width: '100%', maxWidth: 360, p: 3, borderRadius: 2, bgcolor: '#fbfbfb', border: '1px dashed #ccc' }}>
                        {isUae ? (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Order Subtotal (Base)</Typography>
                                    <Typography variant="body2" color="text.primary">{formatCurrency(totals.subtotal, currency)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Service Charge ({uaeRates.serviceChargePct}%)
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        + {formatCurrency(totals.serviceChargeTotal, currency)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Dubai Municipality Fee ({uaeRates.municipalityFeePct}%)
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        + {formatCurrency(totals.municipalityFeeTotal, currency)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            UAE VAT ({uaeRates.vatPct}% on Subtotal)
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}>
                                            Subtotal before VAT: {formatCurrency(totals.subtotalBeforeVat, currency)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.primary">+ {formatCurrency(totals.vatTotal, currency)}</Typography>
                                </Box>
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body1" color="text.secondary">
                                    Total Tax Amount
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">{formatCurrency(totals.taxTotal)}</Typography>
                            </Box>
                        )}
                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                            <Typography variant="h6" fontWeight="bold">Total Amount</Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary.main">{formatCurrency(totals.grandTotal, currency)}</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant="outlined" onClick={onBack} disabled={isSubmitting} size="large" sx={{ borderRadius: 2, px: 3 }}>Cancel</Button>
                            <Button 
                                variant="contained" 
                                onClick={handleSubmit} 
                                disabled={isSubmitting || !form.roomId || isRoomChargeLocked}
                                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                                size="large"
                                sx={{ borderRadius: 2, px: 3, boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)' }}
                            >
                                {isSubmitting ? 'Posting...' : 'Post Order'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
